import React, { useContext, useState, useCallback } from "react";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import {
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Switch,
  Divider,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  LayoutDashboard,
  TableProperties,
  FileText,
  Map,
  Users2Icon,
  Users,
  Code2,
  Badge,
  Database,
  Settings,
  Cog,
  Link2,
  ChevronDown,
  ChevronUp,
  UserCog,
  Eye,
  EyeOff,
  GripVertical,
  SlidersHorizontal,
  X,
  MessageSquare,
  BookOpen,
  TrendingUp,
  ClipboardList,
} from "lucide-react";

import { DRAWER_WIDTH, USER_ROLES } from "../utils/constants";
import { UserContext } from "../context/UserProvider";

// Inline modifier — restricts drag to vertical axis only
const restrictToVerticalAxis = ({ transform }) => ({
  ...transform,
  x: 0,
});

const ICON_SIZE = 20;
const STROKE_WIDTH = 1.75;
const HIDDEN_KEY = "sideMenuHidden_v2";
const ORDER_KEY = "sideMenuOrder_v2";

const DEFAULT_MENU_ITEMS = [
  {
    text: "Dashboard",
    link: "/",
    icon: <LayoutDashboard size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "OES Forms",
    link: "/oes-forms",
    icon: <TableProperties size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "Visitor Form",
    link: "/form-links",
    icon: <FileText size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "Floor Plan",
    link: "/floor-plan-creator",
    icon: <Map size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "Users List",
    link: "/users-list",
    icon: <Users2Icon size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    subcategories: [
      {
        text: "Exhibitor List",
        link: "/exhibitors-list",
        icon: <Users size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
      {
        text: "Visitors List",
        link: "/visitors-list",
        icon: <Users size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
      {
        text: "Trade delegates",
        link: "/manage-delegates",
        icon: <Users size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
      {
        text: "Service Codes",
        link: "/service-codes-list",
        icon: <Code2 size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
      },
    ],
  },
  {
    text: "Database",
    link: "/export-data",
    icon: <Database size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "Settings",
    link: "/settings",
    icon: <Settings size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "OES Explorer",
    link: "/oes-explorer",
    icon: <Cog size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
    role: USER_ROLES.OES,
  },
  {
    text: "Onsite Module",
    link: "/onsite-links",
    icon: <Link2 size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "Social Wall",
    link: "/social-wall",
    icon: <MessageSquare size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "UFI Analytics",
    link: "/ufi-analytics",
    icon: <TrendingUp size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "OES Analytics",
    link: "/oes-analytics",
    icon: <ClipboardList size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
  {
    text: "Catalogue",
    link: "/event-catalogue",
    icon: <BookOpen size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
  },
];

// ─── Persistence helpers ────────────────────────────────────────────────────

const loadHidden = () => {
  try {
    const stored = localStorage.getItem(HIDDEN_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

const saveHidden = (hiddenSet) => {
  try {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(Array.from(hiddenSet)));
  } catch {}
};

const loadOrder = (items) => {
  try {
    const stored = localStorage.getItem(ORDER_KEY);
    if (!stored) return items;
    const order = JSON.parse(stored);
    const map = new Map(items.map((item) => [item.text, item]));
    const sorted = order.map((text) => map.get(text)).filter(Boolean);
    const missing = items.filter((item) => !order.includes(item.text));
    return [...sorted, ...missing];
  } catch {
    return items;
  }
};

const saveOrder = (items) => {
  try {
    localStorage.setItem(ORDER_KEY, JSON.stringify(items.map((i) => i.text)));
  } catch {}
};

// ─── Sortable Sidebar Row Item ──────────────────────────────────────────────

function SortableNavItem({
  item,
  isNested,
  isActive,
  isChildActive,
  isExpanded,
  hasSub,
  linkProps,
  onToggleSubmenu,
  children,
  isDragOverlay,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.text, disabled: isNested });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <ListItemButton
        {...linkProps}
        onClick={(e) => {
          if (hasSub) {
            e.preventDefault();
            onToggleSubmenu(item.text);
          }
        }}
        sx={{
          borderRadius: "8px",
          py: 1.1,
          px: 1.5,
          pl: isNested ? 3.5 : 1.5,
          margin: "2px 0",
          backgroundColor: isActive
            ? "#2563EB !important"
            : isChildActive
            ? "#EFF6FF"
            : "transparent",
          color: isActive
            ? "#FFFFFF !important"
            : isChildActive
            ? "#1D4ED8"
            : "#475467",
          boxShadow: isActive ? "0px 4px 12px rgba(37, 99, 235, 0.25)" : "none",
          "&:hover": {
            backgroundColor: isActive ? "#1D4ED8 !important" : "#F1F5F9",
            color: isActive ? "#FFFFFF" : "#1E293B",
            "& .drag-handle": {
              opacity: 1,
            },
          },
          cursor: isDragOverlay ? "grabbing" : "pointer",
        }}
      >
        {/* Drag handle */}
        {!isNested && (
          <Box
            className="drag-handle"
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              opacity: isDragOverlay ? 1 : 0,
              color: isActive ? "rgba(255,255,255,0.6)" : "#CBD5E1",
              cursor: "grab",
              mr: 0.75,
              flexShrink: 0,
              transition: "opacity 0.15s ease",
              "&:active": { cursor: "grabbing" },
            }}
          >
            <GripVertical size={15} strokeWidth={2} />
          </Box>
        )}

        {/* Icon */}
        <ListItemIcon
          sx={{
            minWidth: 32,
            color: isActive
              ? "#FFFFFF !important"
              : isChildActive
              ? "#2563EB"
              : "#64748B",
          }}
        >
          {item.icon}
        </ListItemIcon>

        {/* Text */}
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            fontSize: isNested ? "0.85rem" : "0.9rem",
            fontWeight: isActive || isChildActive ? 600 : 500,
          }}
        />

        {/* Chevron for expandable parents */}
        {hasSub && (
          <Box
            sx={{
              color: isChildActive ? "#2563EB" : "#94A3B8",
              display: "flex",
              mr: 0.5,
            }}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Box>
        )}
      </ListItemButton>

      {/* Nested collapse */}
      {children}
    </div>
  );
}

// ─── Sortable Settings Dialog Item ──────────────────────────────────────────

function SortableSettingsItem({ item, isEnabled, onToggleVisibility }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.text });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 1.5,
        py: 1,
        borderRadius: "8px",
        bgcolor: isEnabled ? "#FFFFFF" : "#F8FAFC",
        border: "1px solid",
        borderColor: isEnabled ? "#E2E8F0" : "#F1F5F9",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          borderColor: "#CBD5E1",
          bgcolor: isEnabled ? "#F8FAFC" : "#F1F5F9",
          "& .modal-drag-handle": {
            color: "#64748B",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Drag handle inside modal */}
        <Box
          className="modal-drag-handle"
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#CBD5E1",
            cursor: "grab",
            transition: "color 0.15s ease",
            "&:active": { cursor: "grabbing" },
            pr: 0.5,
          }}
        >
          <GripVertical size={16} strokeWidth={2} />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            color: isEnabled ? "#1E293B" : "#94A3B8",
          }}
        >
          <Box
            sx={{
              display: "flex",
              color: isEnabled ? "#2563EB" : "#94A3B8",
            }}
          >
            {item.icon}
          </Box>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: isEnabled ? 500 : 400,
              color: isEnabled ? "#1E293B" : "#64748B",
            }}
          >
            {item.text}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 600,
            color: isEnabled ? "#2563EB" : "#94A3B8",
            bgcolor: isEnabled ? "#EFF6FF" : "#F1F5F9",
            px: 1,
            py: 0.25,
            borderRadius: "4px",
            minWidth: "36px",
            textAlign: "center",
          }}
        >
          {isEnabled ? "ON" : "OFF"}
        </Typography>
        <Switch
          size="small"
          checked={isEnabled}
          onChange={() => onToggleVisibility(item.text)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#2563EB",
            },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#2563EB",
            },
          }}
        />
      </Box>
    </Box>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function SideMenuBar({ open, setOpen }) {
  const location = useLocation();
  const { user } = useContext(UserContext);

  const [openSubmenu, setOpenSubmenu] = useState({ "Users List": false });
  const [hiddenItems, setHiddenItems] = useState(loadHidden);
  const [activeId, setActiveId] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Compute role-filtered base items & order
  const buildBaseItems = useCallback(() => {
    if (user?.role === USER_ROLES.TD_ADMIN) {
      return [
        {
          text: "Manage TDs",
          link: "/manage-delegates",
          icon: <UserCog size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
        },
      ];
    }
    if (user?.role === USER_ROLES.OES || user?.role === "oes") {
      return [
        {
          text: "Dashboard",
          link: "/",
          icon: <LayoutDashboard size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
        },
        {
          text: "OES Forms",
          link: "/oes-forms",
          icon: <TableProperties size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
        },
        {
          text: "OES Analytics",
          link: "/oes-analytics",
          icon: <ClipboardList size={ICON_SIZE} strokeWidth={STROKE_WIDTH} />,
        },
      ];
    }
    if (user?.role === USER_ROLES.ADMIN || user?.role === "admin") {
      const adminAllowedTexts = [
        "Dashboard",
        "OES Forms",
        "Visitor Form",
        "Floor Plan",
        "Users List",
        "Database",
        "Settings",
      ];
      const filtered = DEFAULT_MENU_ITEMS.filter((item) =>
        adminAllowedTexts.includes(item.text)
      );
      return loadOrder(filtered);
    }
    const filtered = DEFAULT_MENU_ITEMS.filter(
      (item) => !item.role || item.role === user?.role
    );
    return loadOrder(filtered);
  }, [user?.role]);

  const [menuItems, setMenuItems] = useState(buildBaseItems);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleToggleSubmenu = (itemText) => {
    setOpenSubmenu((prev) => ({ ...prev, [itemText]: !prev[itemText] }));
  };

  const handleToggleVisibility = (itemText) => {
    setHiddenItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemText)) {
        next.delete(itemText);
      } else {
        next.add(itemText);
      }
      saveHidden(next);
      return next;
    });
  };

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    setMenuItems((prev) => {
      const oldIndex = prev.findIndex((i) => i.text === active.id);
      const newIndex = prev.findIndex((i) => i.text === over.id);
      const next = arrayMove(prev, oldIndex, newIndex);
      saveOrder(next);
      return next;
    });
  };

  const handleDragCancel = () => setActiveId(null);

  const isLinkActive = (link) => {
    if (!link) return false;
    if (link === "/") return location.pathname === "/";
    return location.pathname === link || location.pathname.startsWith(link);
  };

  const buildLinkProps = (item) => {
    if (!item.link) return {};
    if (item.link.startsWith("http")) {
      return {
        component: "a",
        href: item.link,
        target: "_blank",
        rel: "noopener noreferrer",
      };
    }
    return { component: RouterLink, to: item.link };
  };

  // Filter items visible in main menu
  const visibleMenuItems = menuItems.filter(
    (item) => !hiddenItems.has(item.text)
  );

  const renderNavList = (items, isNested = false) => {
    return items.map((item) => {
      const hasSub =
        Array.isArray(item.subcategories) && item.subcategories.length > 0;
      const isExpanded = Boolean(openSubmenu[item.text]);
      const isActive = isLinkActive(item.link);
      const isChildActive =
        hasSub && item.subcategories.some((s) => isLinkActive(s.link));
      const linkProps = buildLinkProps(item);

      const nestedList = hasSub && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ pl: 0.5 }}>
            {renderNavList(item.subcategories, true)}
          </List>
        </Collapse>
      );

      return (
        <SortableNavItem
          key={item.text}
          item={item}
          isNested={isNested}
          isActive={isActive}
          isChildActive={isChildActive}
          isExpanded={isExpanded}
          hasSub={hasSub}
          linkProps={linkProps}
          onToggleSubmenu={handleToggleSubmenu}
        >
          {nestedList}
        </SortableNavItem>
      );
    });
  };

  const draggedItem = activeId
    ? menuItems.find((i) => i.text === activeId)
    : null;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        transition: (theme) =>
          theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        "& .MuiDrawer-paper": {
          width: open ? DRAWER_WIDTH : 0,
          overflowX: "hidden",
          boxSizing: "border-box",
          top: 70,
          height: "calc(100vh - 70px)",
          borderRight: open ? "1px solid #E7EAF0" : "none",
          backgroundColor: "#FFFFFF",
          visibility: open ? "visible" : "hidden",
          transition: (theme) =>
            theme.transitions.create(["width", "visibility"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          pt: 1.5,
          pb: 2,
          px: 1.5,
          backgroundColor: "#FFFFFF",
        }}
      >
        {/* Navigation List */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            pr: 0.5,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext
              items={visibleMenuItems.map((i) => i.text)}
              strategy={verticalListSortingStrategy}
            >
              <List
                component="nav"
                sx={{ p: 0, gap: 0.2, display: "flex", flexDirection: "column" }}
              >
                {renderNavList(visibleMenuItems)}
              </List>
            </SortableContext>

            {/* Drag Overlay */}
            <DragOverlay dropAnimation={null}>
              {draggedItem ? (
                <Box
                  sx={{
                    bgcolor: "#ffffff",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                    boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
                    px: 1.5,
                    py: 1.1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#1E293B",
                    cursor: "grabbing",
                  }}
                >
                  <GripVertical size={15} strokeWidth={2} color="#CBD5E1" />
                  <Box sx={{ color: "#64748B", display: "flex" }}>
                    {draggedItem.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      flex: 1,
                      color: "#475467",
                    }}
                  >
                    {draggedItem.text}
                  </Typography>
                </Box>
              ) : null}
            </DragOverlay>
          </DndContext>
        </Box>

        {/* Footer Settings Button */}
        <Box sx={{ pt: 1.5, borderTop: "1px solid #F1F5F9" }}>
          <Button
            fullWidth
            onClick={() => setIsSettingsOpen(true)}
            startIcon={
              <SlidersHorizontal size={17} strokeWidth={STROKE_WIDTH} />
            }
            sx={{
              borderRadius: "8px",
              py: 1,
              px: 1.5,
              justifyContent: "flex-start",
              textTransform: "none",
              color: "#475467",
              fontSize: "0.875rem",
              fontWeight: 500,
              bgcolor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              "&:hover": {
                bgcolor: "#EFF6FF",
                borderColor: "#BFDBFE",
                color: "#2563EB",
              },
            }}
          >
            Navigation Settings
          </Button>
        </Box>

        {/* Navigation Settings Dialog */}
        <Dialog
          open={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              p: 0.5,
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              pb: 1,
              pt: 2,
              px: 2.5,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: "1.05rem",
                  color: "#0F172A",
                  lineHeight: 1.2,
                }}
              >
                Navigation Settings
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#64748B", fontSize: "0.8rem", mt: 0.5 }}
              >
                Manage & reorder menus in your navigation.
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setIsSettingsOpen(false)}
              sx={{ color: "#94A3B8", "&:hover": { bgcolor: "#F1F5F9" } }}
            >
              <X size={18} />
            </IconButton>
          </DialogTitle>

          <Divider sx={{ mx: 2, my: 0.5, borderColor: "#F1F5F9" }} />

          <DialogContent sx={{ px: 2, py: 1, maxHeight: "420px" }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={menuItems.map((i) => i.text)}
                strategy={verticalListSortingStrategy}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                >
                  {menuItems.map((item) => (
                    <SortableSettingsItem
                      key={item.text}
                      item={item}
                      isEnabled={!hiddenItems.has(item.text)}
                      onToggleVisibility={handleToggleVisibility}
                    />
                  ))}
                </Box>
              </SortableContext>

              <DragOverlay dropAnimation={null}>
                {draggedItem ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 1,
                      borderRadius: "8px",
                      bgcolor: "#FFFFFF",
                      border: "1px solid #2563EB",
                      boxShadow: "0 10px 20px rgba(37, 99, 235, 0.15)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <GripVertical size={16} color="#2563EB" />
                      <Box sx={{ color: "#2563EB", display: "flex" }}>
                        {draggedItem.icon}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "#1E293B",
                        }}
                      >
                        {draggedItem.text}
                      </Typography>
                    </Box>
                  </Box>
                ) : null}
              </DragOverlay>
            </DndContext>
          </DialogContent>

          <Box
            sx={{
              p: 2,
              pt: 1,
              borderTop: "1px solid #F1F5F9",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              size="small"
              onClick={() => {
                setHiddenItems(new Set());
                saveHidden(new Set());
              }}
              sx={{
                fontSize: "0.75rem",
                color: "#64748B",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": { color: "#2563EB", bgcolor: "#EFF6FF" },
              }}
            >
              Enable All
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setIsSettingsOpen(false)}
              sx={{
                borderRadius: "6px",
                textTransform: "none",
                fontSize: "0.825rem",
                fontWeight: 500,
                bgcolor: "#2563EB",
                boxShadow: "none",
                "&:hover": { bgcolor: "#1D4ED8", boxShadow: "none" },
              }}
            >
              Done
            </Button>
          </Box>
        </Dialog>
      </Box>
    </Drawer>
  );
}
